import { useRef } from 'react';
import { BRAND } from '@repo/shared/brand';
import { GENDERS } from '@repo/shared/domain';
import { PLANS } from '@repo/shared/plans';
import type { SettingsUpdate } from '../App.tsx';
import type { BillingNotice } from '../hooks/useEntitlement.ts';
import { useMagnetic, usePointerLight } from '../hooks/usePointerEffects.ts';
import { GENDER_LABELS } from '../labels.ts';
import type { Entitlement } from '../lib/entitlement.ts';
import type { Settings } from '../lib/settings.ts';
import { Icon } from './Icon.tsx';
import { HeroVisual, Marquee } from './Stage.tsx';

interface LandingProps {
  settings: Settings;
  starting: boolean;
  error: string | null;
  entitlement: Entitlement | null;
  /** Rückmeldung nach der Rückkehr von der Stripe-Bezahlseite */
  notice: BillingNotice | null;
  onDismissNotice: () => void;
  onSettingsChange: SettingsUpdate;
  onStart: (useTestStream: boolean) => void;
}

export function Landing({
  settings,
  starting,
  error,
  entitlement,
  notice,
  onDismissNotice,
  onSettingsChange,
  onStart,
}: LandingProps) {
  const ready = settings.gender !== null && settings.adultConfirmed && !starting;
  const heroRef = useRef<HTMLElement>(null);
  const startRef = useRef<HTMLButtonElement>(null);
  usePointerLight(heroRef);
  useMagnetic(startRef);

  return (
    <main className="landing">
      <div className="aurora" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <header className="landing__nav">
        <span className="wordmark">
          <span className="logo" aria-hidden="true" />
          {BRAND.name}
        </span>
        {entitlement && entitlement.plan !== 'free' ? (
          <span className="plan-badge">
            <Icon name="crown" size={14} /> {PLANS[entitlement.plan].name} aktiv
          </span>
        ) : (
          <span className="eyebrow">
            <span className="eyebrow__dot" aria-hidden="true" />
            Live
          </span>
        )}
      </header>

      <section ref={heroRef} className="landing__hero">
        <div className="landing__intro">
          <p className="eyebrow">Videochat mit Menschen, die du noch nicht kennst</p>
          <h1 className="display">
            <span className="line">
              <span>Sag Hallo zu</span>
            </span>
            <span className="line">
              <span>
                <em>jemand Neuem.</em>
              </span>
            </span>
          </h1>
          <p className="lead">
            Ein Klick und du sitzt jemandem gegenüber, den du sonst nie getroffen hättest. Aus
            deiner Stadt oder vom anderen Ende der Welt.
          </p>

          <div className="join">
            {notice && (
              <div className={`banner banner--${notice.kind}`} role="status">
                <span>{notice.text}</span>
                <button
                  type="button"
                  className="icon-button"
                  onClick={onDismissNotice}
                  aria-label="Hinweis schließen"
                >
                  <Icon name="close" size={16} />
                </button>
              </div>
            )}

            <fieldset className="field">
              <legend>Ich bin</legend>
              <div className="segmented">
                {GENDERS.map((gender) => (
                  <button
                    key={gender}
                    type="button"
                    aria-pressed={settings.gender === gender}
                    onClick={() => onSettingsChange((current) => ({ ...current, gender }))}
                  >
                    {GENDER_LABELS[gender]}
                  </button>
                ))}
              </div>
              <p className="hint">Das sieht niemand. Es hilft nur bei den Filtern der anderen.</p>
            </fieldset>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={settings.adultConfirmed}
                onChange={(event) => {
                  const adultConfirmed = event.target.checked;
                  onSettingsChange((current) => ({ ...current, adultConfirmed }));
                }}
              />
              <span>Ich bin mindestens 18 und bleibe respektvoll.</span>
            </label>

            <div className="landing__actions">
              <button
                ref={startRef}
                type="button"
                className="button button--primary button--large"
                disabled={!ready}
                onClick={() => onStart(false)}
              >
                {starting ? 'Kamera startet' : 'Kamera an und los'}
                <span className="button__arrow">
                  <Icon name="next" size={18} />
                </span>
              </button>
              {import.meta.env.DEV && (
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={!ready}
                  onClick={() => onStart(true)}
                >
                  Mit Testbild starten (Dev)
                </button>
              )}
            </div>

            {error && (
              <p className="alert" role="alert">
                {error}
              </p>
            )}
          </div>
        </div>

        <HeroVisual />
      </section>

      <Marquee />

      <ol className="facts">
        <li>
          <span className="facts__num">01</span>
          <h3>Direkt zwischen euch</h3>
          <p>Euer Video läuft von Gerät zu Gerät. Wir schauen nicht zu und speichern nichts.</p>
        </li>
        <li>
          <span className="facts__num">02</span>
          <h3>Passt nicht? Weiter.</h3>
          <p>
            Ein Tipp und du bist beim nächsten Menschen. Wer sich danebenbenimmt, wird gemeldet.
          </p>
        </li>
        <li>
          <span className="facts__num">03</span>
          <h3>Nah oder weit weg</h3>
          <p>Deine Stadt, dein Land oder die ganze Welt. Du entscheidest, wen du triffst.</p>
        </li>
      </ol>
    </main>
  );
}
